import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { NewRequestService } from "@edp-pages/requests-and-operations/services/new-request.service";
import { Service } from "@edp-models/service.model";
import { StepsService } from "@edp-pages/requests-and-operations/services/steps.service";
import { NewModalService } from "@edp-pages/requests-and-operations/services/new-modal.service";
import { select, Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { SetSelectedServiceAndMotive } from "@edp-store/actions/requests-and-operations.actions";
import { getSelectedServiceAndMotive } from "@edp-store/selectors/requests-and-operations.selector";
import { removeSpace } from "@edp-utils/remove-space.util";
import { removeSpecialCharacters } from "@edp-utils/generate.label.util";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import { takeUntil } from "rxjs/operators";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Subject } from "rxjs";
import { Contract } from "@edp-components/edp-contract";
import { TypeEventOperationHistory } from "@edp-enums/type-event-operation.enum";
@Component({
    selector: "request-motives-selection",
    templateUrl: "./request-motives-selection.component.html",
    styleUrls: ["./request-motives-selection.component.scss"],
})
export class RequestMotivesSelectionComponent implements OnInit, OnDestroy {
    private unsubscribe$ = new Subject();
    private selectedMotiveId: string;
    private contract: Contract;


    constructor(
        private readonly newRequestService: NewRequestService,
        private readonly stepService: StepsService,
        private readonly newModalService: NewModalService,
        private readonly store: Store<IAppState>,
        private operationHistoryService: OperationHistoryService
    ) {
        this.store.pipe(select(getSelectedServiceAndMotive)).subscribe(newRequest => {
            this.selectedMotiveId = !!newRequest && !!newRequest.motiveId ? newRequest.motiveId : null;
        });
    }
   
    lookupRecurssive = true;
    loadedService: Service;
    loadedMotives: Service[];
    nodePath: {
        breadcrumb: string[];
        motives: [Service[]];
    };
    position = 0;
    isPrevious = false;
    selectedexternalCategoryId: string;
    selectedID: string;
    parentNode: Service;

    @Input() set selectedService(selectedService: Service) {
        this.loadedService = selectedService;
        this.setInitMotives(selectedService);
    }

    ngOnInit(): void {
        const requestAndService = this.newRequestService.getRequestAndService().value;
        if (requestAndService) {
            this.selectMotiveFromParams(requestAndService.selectedService, requestAndService.selectedMotive.id);
        } else if (this.newModalService.getPopupData() && this.newModalService.getPopupData().selectedMotive) {
            // from url params
            this.selectMotiveFromParams(this.loadedService, this.newModalService.getPopupData().selectedMotive);
            this.stepService.setCurrentStep();
        }
        this.getContractId();
    }

    private setInitMotives(service: Service) {
        if (service && this.isChildrenAndLast(service.childs)) {
            this.loadedMotives = service.childs;
            this.nodePath = {
                breadcrumb: [service.name],
                motives: [this.loadedMotives],
            };
            this.position = 0;
            this.isPrevious = false;
        }
    }

    private findById(motive, id: string) {
        let founded: boolean;
        for (const iterator of motive) {
            if (this.lookupRecurssive) {
                if ((iterator.externalCategory && iterator.externalCategory === id) || iterator.id === id) {
                    this.loadedMotives = motive;
                    this.lookupRecurssive = false;
                    this.forwardSelection(iterator);
                    return true;
                } else if (iterator.childs && iterator.childs.length) {
                    this.parentNode = iterator;
                    this.forwardSelection(iterator);
                    founded = this.findById(iterator.childs, id);
                }
            }
        }
        if (!founded && this.lookupRecurssive) {
            this.nodePop();
        }
    }

    public isChildrenAndLast(node: Service[]) {
        return node && node.length;
    }

    public selectMotive(motive: Service) {
        this.selectedID = motive.id;
        this.addState(motive);
        this.stepService.setAdvanceStepButtonAvailability(true);
        this.setTypeEventOperation(motive.name);
    }

    public forwardSelection(node: Service) {
        this.selectMotive(node);
        if (node.childs) {
            this.moveNodePath(true, node);
            this.loadedMotives = node.childs;
            this.stepService.setAdvanceStepButtonAvailability(false);
        }
    }

    public backSelection() {
        if (this.nodePath.motives.length > 1) {
            this.moveNodePath(false);
            this.loadedMotives = this.nodePath.motives[this.nodePath.motives.length - 1];
            this.removeState();
            this.stepService.setAdvanceStepButtonAvailability(false);
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    private moveNodePath(forward = true, node?) {
        if (forward) {
            this.position++;
            if (node.childs) {
                this.nodePath.motives.push(node.childs);
                this.nodePath.breadcrumb.push(node.name);
            } else {
                this.nodePath.breadcrumb.push(this.parentNode.name);
            }
        } else {
            this.nodePop();
        }
    }

    private nodePop() {
        this.nodePath.motives.pop();
        this.nodePath.breadcrumb.pop();
        this.position--;
    }

    private selectMotiveFromParams(selectedService: Service, motiveId: string) {
        if (!this.selectedMotiveId) {
            this.findById(selectedService.childs, motiveId);
            this.lookupRecurssive = true;
        } else {
            this.setSelectedMotive(selectedService.childs, motiveId);
        }
    }

    private addState(motive: Service) {
        this.newRequestService.addServiceAndMotive(this.loadedService, motive);

        const payload = {
            selectedService: this.loadedService,
            selectedMotive: motive,
        };

        !!this.selectedMotiveId
            ? this.store.dispatch(new SetSelectedServiceAndMotive({ ...payload, ...{ motiveId: this.selectedMotiveId } }))
            : this.store.dispatch(new SetSelectedServiceAndMotive(payload));
    }

    private removeState() {
        this.newRequestService.removeServiceAndMotive();
    }

    private setSelectedMotive(motives, externalCategory) {
        motives.forEach(el => {
            el.childs.forEach(el2 => {
                const motive = el2.childs.find(el3 => el3.externalCategory === externalCategory && el3.motiveId === this.selectedMotiveId);
                if (!!motive) {
                    this.selectMotive(motive);
                    return;
                }
            });
        });
    }

    private getContractId() {
        this.store.pipe(takeUntil(this.unsubscribe$), select(getContract)).subscribe(contract => {
            if (!!contract) {
                this.contract = contract;
            }
        });
    }
    
    private setTypeEventOperation(label: string) {
        const type = `${removeSpecialCharacters(removeSpace(label.toUpperCase()))}`;
        this.sendOperationHistoryService(TypeEventOperationHistory[type]);
    }
    
    private sendOperationHistoryService(key: string, value: string | null = null) {
        this.operationHistoryService.registerWithContract(this.contract?.ContractId, key, value, null, null, false);
    }
    
}
